import { useState, useCallback } from 'react';
import { useAuthenticatedFetchWithWorkspace } from '../Keycloak/useAuthenticatedFetchWithWorkspace';
import { QueryGroup, QuerySource } from '../../types/QueryBuilder';
import { convertGroupsToBackendFormat } from '../../utils/queryBuilderUtils';
import { useQueryBuilderCache } from '../../context/QueryBuilderCacheContext';

interface SaveAndCountParams {
    description: string;
    groups: QueryGroup[];
    groupId?: number | null;
    source?: QuerySource;
}

export type SaveAndCountResult =
    | { mode: 'async'; taskId: string; queryId?: number }
    | { mode: 'sync'; queryId: number; count: number };

export const useSaveAndCountQuery = () => {
    const { authFetch, isReady } = useAuthenticatedFetchWithWorkspace();
    const cache = useQueryBuilderCache();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const saveAndCountQuery = useCallback(async ({ description, groups, groupId, source }: SaveAndCountParams): Promise<SaveAndCountResult | null> => {
        if (!isReady || !groups?.length || !groups.some(g => g.rules?.length)) return null;

        setLoading(true);
        setError(null);

        try {
            const response = await authFetch('/saveAndCountQuery', {
                method: 'POST',
                body: JSON.stringify({
                    query_id: 0,
                    queryName: description,
                    description,
                    query_type: 1,
                    source: source ?? 'SIMPLE',
                    conditions: [convertGroupsToBackendFormat(groups)],
                    ...(groupId && groupId > 0 && { group_query_id: groupId }),
                }),
            });

            cache?.invalidate('saved_queries|');

            if (response?.taskId) return { mode: 'async', taskId: response.taskId, queryId: response.queryId };
            if (response?.queryId !== undefined && response?.count !== undefined) return { mode: 'sync', queryId: response.queryId, count: response.count };

            throw new Error('Réponse invalide du serveur');
        } catch (err) {
            setError(err instanceof Error ? err.message : "Erreur lors de l'exécution");
            return null;
        } finally {
            setLoading(false);
        }
    }, [authFetch, isReady, cache]);

    return { saveAndCountQuery, loading, error };
};

